import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next'; // Added

// Import caption style previews
import karaokeDefaultPreview from '../../assets/caption-styles/karaoke_default_v1.gif';
import karaokePreview from '../../assets/caption-styles/karaoke_v1.gif';
import karaokeStablePreview from '../../assets/caption-styles/karaoke_stable_v1.gif';
import karaokeContinuousPreview from '../../assets/caption-styles/karaoke_continuous_v1.gif';
import verticalKeyPhrasesPreview from '../../assets/caption-styles/vertical_key_phrases_v1.gif';
import mrbeastPreview from '../../assets/caption-styles/mrbeast_v1.gif';
import hormoziPreview from '../../assets/caption-styles/hormozi_v1.gif';
import arsenPreview from '../../assets/caption-styles/arsen_v1.gif';
import arsenKeyPhrasesPreview from '../../assets/caption-styles/arsen_key_phrases_v1.gif';
import { lightTokens } from '../../theme/presets'; // Added import

// Define available caption styles
const CAPTION_STYLES = [
  {
    id: 'karaoke_default_v1',
    preview: karaokeDefaultPreview,
  },
  {
    id: 'karaoke_v1',
    preview: karaokePreview,
  },
  {
    id: 'karaoke_stable_v1',
    preview: karaokeStablePreview,
  },
  {
    id: 'karaoke_continuous_v1',
    preview: karaokeContinuousPreview,
  },
  {
    id: 'vertical_key_phrases_v1',
    preview: verticalKeyPhrasesPreview,
  },
  {
    id: 'mrbeast_v1',
    preview: mrbeastPreview,
  },
  {
    id: 'hormozi_v1',
    preview: hormoziPreview,
  },
  {
    id: 'arsen_v1',
    preview: arsenPreview,
  },
  {
    id: 'arsen_key_phrases_v1',
    preview: arsenKeyPhrasesPreview,
  },
];

interface CaptionsStyleSelectorProps {
  selected: string;
  onSelectStyle: (styleId: string) => void;
  color?: string;
  accentColor?: string;
  fontSize?: number;
  accentFontSize?: number;
  onColorChange: (type: 'color' | 'accent_color', value: string | undefined) => void;
  onFontSizeChange: (type: 'font_size' | 'accent_font_size', value: number | undefined) => void;
  onResetCustomizations: () => void;
}

export function CaptionsStyleSelector({
  selected,
  onSelectStyle,
  color,
  accentColor,
  fontSize,
  accentFontSize,
  onColorChange,
  onFontSizeChange,
  onResetCustomizations
}: CaptionsStyleSelectorProps) {
  const { t } = useTranslation(); // Added

  // State for customization toggle
  const [useCustomCaptionSettings, setUseCustomCaptionSettings] = useState(
    !!(color || accentColor || fontSize || accentFontSize)
  );

  // Effect to update state if props change externally
  useEffect(() => {
    setUseCustomCaptionSettings(!!(color || accentColor || fontSize || accentFontSize));
  }, [color, accentColor, fontSize, accentFontSize]);

  // Handler for the "Customize" checkbox
  const handleCustomizeToggle = (isChecked: boolean) => {
    setUseCustomCaptionSettings(isChecked);
    if (!isChecked) {
      // If disabling customization, reset colors to undefined and call the new reset function
      onColorChange('color', undefined);
      onColorChange('accent_color', undefined);
      onResetCustomizations();
    } else {
      // When enabling, if colors are undefined, set default picker values
      // The parent state won't update until a color is actually chosen
      if (color === undefined) {
        // Maybe set a default visual for the picker if needed, but don't call onColorChange yet
      }
      if (accentColor === undefined) {
        // Maybe set a default visual for the picker if needed
      }
    }
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      <h3 className="text-base font-medium text-textPrimary">
        {t('captionsStyleSelector.sectionTitle')}
      </h3>

      {/* Caption styles grid - responsive for different screen sizes */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 md:gap-3 flex-grow">
        {CAPTION_STYLES.map((style) => (
          <div
            key={style.id}
            className={`relative aspect-[9/16] rounded-lg overflow-hidden cursor-pointer ${
              selected === style.id ? 'ring-2 ring-blue-500' : ''
            } hover:opacity-90 transition-all max-w-[120px] md:max-w-[160px] mx-auto w-full`}
            onClick={() => onSelectStyle(style.id)}
          >
            <img
              src={style.preview}
              alt={t('captionsStyleSelector.imageAltText', { styleId: style.id })}
              className="w-full h-full object-cover"
            />
            {selected === style.id && (
              <div className="absolute top-2 right-2 bg-blue-500 rounded-full">
                <span className="material-symbols-outlined text-white p-1">check_circle</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Customize Checkbox - Moved here */}
      <div className="mt-4 mb-3 border-t border-border pt-4">
         <div className="flex items-center">
           <div className="w-5 h-5 mr-2 relative">
             <input
               type="checkbox"
               id="customize-captions-selector" // Unique ID
               checked={useCustomCaptionSettings}
               onChange={(e) => handleCustomizeToggle(e.target.checked)}
               className="sr-only peer"
             />
             <label 
               htmlFor="customize-captions-selector" 
               className="flex items-center justify-center w-5 h-5 rounded peer-checked:bg-primary bg-surface border border-border peer-checked:border-primary cursor-pointer"
             >
               {useCustomCaptionSettings && (
                 <span className="material-symbols-outlined text-textOnPrimary text-xs">check</span>
               )}
             </label>
           </div>
           <label htmlFor="customize-captions-selector" className="text-sm text-textPrimary cursor-pointer">{t('captionsStyleSelector.customizeLabel')}</label>
         </div>
      </div>

      {/* Conditionally render advanced controls - Moved here */}
      {useCustomCaptionSettings && (
        <div className="space-y-4 p-4 bg-bgSecondary rounded-lg mt-2 border border-border">
          {/* Font Color */}
          <div className="flex flex-col">
            <label className="mb-2 text-sm text-textPrimary">{t('captionsStyleSelector.fontColorLabel')}</label>
            <div className="flex items-center">
              <div className="relative w-8 h-8 mr-2 border border-border rounded-full flex items-center justify-center bg-surface">
                 {color ? (
                   <div className="absolute inset-0.5 rounded-full overflow-hidden">
                     <div className="w-full h-full" style={{ backgroundColor: color }}></div>
                   </div>
                 ) : (
                   <span className="text-xs text-textHint italic">{t('captionsStyleSelector.defaultColorVisual')}</span>
                 )}
                <input
                  type="color"
                  value={color || lightTokens.textOnPrimary} // Default for picker UI using token
                  onChange={(e) => onColorChange('color', e.target.value)}
                  className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                />
              </div>
              <span className="text-sm text-textPrimary font-mono">
                {color ? color.toUpperCase() : t('captionsStyleSelector.defaultColorText')}
              </span>
            </div>
          </div>

          {/* Accent Color */}
          <div className="flex flex-col">
            <label className="mb-2 text-sm text-textPrimary">{t('captionsStyleSelector.accentColorLabel')}</label>
            <div className="flex items-center">
               <div className="relative w-8 h-8 mr-2 border border-border rounded-full flex items-center justify-center bg-surface">
                 {accentColor ? (
                   <div className="absolute inset-0.5 rounded-full overflow-hidden">
                     <div className="w-full h-full" style={{ backgroundColor: accentColor }}></div>
                   </div>
                 ) : (
                   <span className="text-xs text-textHint italic">{t('captionsStyleSelector.defaultColorVisual')}</span>
                 )}
                <input
                  type="color"
                  value={accentColor || lightTokens.primary} // Default for picker UI using token
                  onChange={(e) => onColorChange('accent_color', e.target.value)}
                  className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                />
              </div>
              <span className="text-sm text-textPrimary font-mono">
                {accentColor ? accentColor.toUpperCase() : t('captionsStyleSelector.defaultColorText')}
              </span>
            </div>
          </div>

          {/* Font Sizes */}
          <div className="flex flex-col mt-4 space-y-4">
            <div className="flex flex-col">
              <label className="mb-2 text-sm text-textPrimary">{t('captionsStyleSelector.fontSizeLabel')}</label>
              <input
                type="number"
                min={10}
                max={100}
                value={fontSize ?? 24}
                onChange={(e) => onFontSizeChange('font_size', e.target.value === '' ? undefined : Number(e.target.value))}
                className="w-full border border-border rounded px-2 py-1 bg-surface text-textPrimary"
              />
            </div>

            <div className="flex flex-col">
              <label className="mb-2 text-sm text-textPrimary">{t('captionsStyleSelector.accentFontSizeLabel')}</label>
              <input
                type="number"
                min={10}
                max={100}
                value={accentFontSize ?? 20}
                onChange={(e) => onFontSizeChange('accent_font_size', e.target.value === '' ? undefined : Number(e.target.value))}
                className="w-full border border-border rounded px-2 py-1 bg-surface text-textPrimary"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
